"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
} from "@mui/material"
import { CheckCircle, Schedule, Assignment } from "@mui/icons-material"
import OnboardingDocuments from "./OnboardingDocuments"
import OnboardingOrientation from "./OnboardingOrientation"
import OnboardingCourses from "./OnboardingCourses"
import OnboardingProfile from "./OnboardingProfile"
import OnboardingCertificate from "./OnboardingCertificate"
// import { getOnboardingProgress } from "../../../Services/OnboardingService2"
// import { getAllEmployees } from "../../Services/LeaveService"
import styles from "./EmployeeOnboardingView.module.css"
import { getOnboardingProgress } from "../../api/OnboardingService2"

interface EmployeeOnboardingViewProps {
  employeeId: string
}

const onboardingSteps = [
  {
    label: "Document Upload",
    description: "Upload required documents and forms",
    component: "documents",
  },
  {
    label: "Orientation",
    description: "Complete orientation and physical clearances",
    component: "orientation",
  },
  {
    label: "Training Courses",
    description: "Complete mandatory training courses",
    component: "courses",
  },
  {
    label: "Employee Profile",
    description: "Complete your detailed employee profile",
    component: "profile",
  },
  {
    label: "Certificate",
    description: "Receive your onboarding completion certificate",
    component: "certificate",
  },
]

const EmployeeOnboardingView: React.FC<EmployeeOnboardingViewProps> = ({ employeeId }) => {
  console.log("EmployeeOnboardingView rendered for employeeId:", employeeId)
  const [activeStep, setActiveStep] = useState(0)
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [onboardingData, setOnboardingData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchOnboardingProgress()
  }, [])

  // const fetchOnboardingProgress = async () => {
  //   try {
  //     setLoading(true)
  //     const data = await getOnboardingProgress(employeeId)
  //     console.log("Onboarding progress data:", data)
  //     setOnboardingData(data)
      
  //     // Parse and subtract 1 safely
  //   const completed = parseInt(data.completed_steps || "0", 10);
  //   const steps = completed > 0 ? [completed - 1] : [];
  //     // setCompletedSteps((prev) => [...new Set([...prev, ...steps])]);
  //     setCompletedSteps(steps)
  //     console.log("Parsed completed steps:", steps)

  //     console.log("Completed steps:", data.completed_steps)
  //     console.log("Parsed completed steps:", completedSteps)
  //     console.log("length of completed steps:", completedSteps.length)

  //     // Set active step to first incomplete step
  //     const nextIncompleteStep = onboardingSteps.findIndex((_, index) => !steps?.includes(index))
  //     console.log("Next incomplete step index:", nextIncompleteStep)
  //     setActiveStep(nextIncompleteStep >= 0 ? nextIncompleteStep : onboardingSteps.length - 1)
  //   } catch (err) {
  //     setError("Failed to load onboarding progress")
  //     console.error(err)
  //   } finally {
  //     setLoading(false)
  //   }
  // }
  
  const fetchOnboardingProgress = async () => {
    try {
      setLoading(true)
      const data = await getOnboardingProgress(employeeId)
      console.log("Onboarding progress data:", data)
      setOnboardingData(data)
      // Handle completed_steps conversion - convert to number and subtract 1 to start from 0
      let completedStepsArray: number[] = []

      if (data.completed_steps) {
        if (Array.isArray(data.completed_steps)) {
          // If it's already an array, convert each item to number and subtract 1
          completedStepsArray = data.completed_steps
            .map((step: any) => {
              const stepNum = Number.parseInt(step.toString(), 10)
              return stepNum > 0 ? stepNum - 1 : -1 // Return -1 for invalid steps
            })
            .filter((step: number) => step >= 0 && step < onboardingSteps.length)
        } else {
          // If it's a single value, convert to number and subtract 1
          const stepNum = Number.parseInt(data.completed_steps.toString(), 10)
          if (stepNum > 1) {
            // Only create completed steps array if stepNum is greater than 1
            // stepNum = 1 means user is on first step (index 0) but hasn't completed it yet
            completedStepsArray = Array.from({ length: stepNum - 1 }, (_, i) => i)
          }
          // If stepNum = 1, completedStepsArray remains empty (no steps completed yet)
        }
      }

      setCompletedSteps(completedStepsArray)
      console.log("Original completed_steps:", data.completed_steps)
      console.log("Converted completed steps array:", completedStepsArray)

      // Set active step - if endpoint returns "1", set active step to 0 (first step)
      const currentStepFromAPI = Number.parseInt(data.completed_steps?.toString() || "1", 10)
      const activeStepIndex = Math.max(0, currentStepFromAPI - 1)
      setActiveStep(Math.min(activeStepIndex, onboardingSteps.length - 1))
    } catch (err) {
      setError("Failed to load onboarding progress")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleStepComplete = (stepIndex: number) => {
    if (!completedSteps.includes(stepIndex)) {
      setCompletedSteps([...completedSteps, stepIndex])
    }

    // Move to next step if not the last one
    if (stepIndex < onboardingSteps.length - 1) {
      setActiveStep(stepIndex + 1)
    }
  }

  const handleStepClick = (stepIndex: number) => {
    setActiveStep(stepIndex)
  }

  const getStepIcon = (stepIndex: number) => {
    if (completedSteps.includes(stepIndex)) {
      return <CheckCircle color="success" />
    }
    if (stepIndex === activeStep) {
      return <Schedule color="primary" />
    }
    return <Assignment color="disabled" />
  }

  const renderStepContent = (stepIndex: number) => {
    const step = onboardingSteps[stepIndex]
    const isCompleted = completedSteps.includes(stepIndex)
    console.log("completedSteps:", completedSteps)
    console.log("isCompleted for step", stepIndex, ":", isCompleted)

    switch (step.component) {
      case "documents":
        return (
          <OnboardingDocuments
            employeeId={employeeId}
            isCompleted={isCompleted}
            onComplete={() => handleStepComplete(stepIndex)}
          />
        )
      case "orientation":
        return (
          <OnboardingOrientation
            employeeId={employeeId}
            isCompleted={isCompleted}
            onComplete={() => handleStepComplete(stepIndex)}
          />
        )
      case "courses":
        return (
          <OnboardingCourses
            employeeId={employeeId}
            isCompleted={isCompleted}
            onComplete={() => handleStepComplete(stepIndex)}
          />
        )
      case "profile":
        return (
          <OnboardingProfile
            employeeId={employeeId}
            isCompleted={isCompleted}
            onComplete={() => handleStepComplete(stepIndex)}
          />
        )
      case "certificate":
        return (
          <OnboardingCertificate employeeId={employeeId} 
          isCompleted={isCompleted} 
           onComplete={() => handleStepComplete(stepIndex)}
          onboardingData={onboardingData} />
        )
      default:
        return null
    }
  }

  const calculateProgress = () => {
    return (completedSteps.length / onboardingSteps.length) * 100
  }

  if (loading) {
    return (
      <Box className={styles.loading}>
        <Typography>Loading onboarding progress...</Typography>
        <LinearProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" className={styles.error}>
        {error}
      </Alert>
    )
  }

  const progress = calculateProgress()
  const isComplete = completedSteps.length === onboardingSteps.length

  return (
    <div className={styles.container}>
      <Card className={styles.progressCard}>
        <CardContent>
          <Box className={styles.progressHeader}>
            <Typography variant="h6">Onboarding Progress</Typography>
            <Chip
              label={isComplete ? "Complete" : `${completedSteps.length}/${onboardingSteps.length} Steps`}
              color={isComplete ? "success" : "primary"}
              variant={isComplete ? "filled" : "outlined"}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            className={styles.progressBar}
            color={isComplete ? "success" : "primary"}
          />
          <Typography variant="body2" color="textSecondary" className={styles.progressText}>
            {Math.round(progress)}% Complete
          </Typography>
        </CardContent>
      </Card>

      {isComplete && (
        <Alert severity="success" className={styles.completionAlert}>
          <Typography variant="h6">Congratulations!</Typography>
          <Typography>You have successfully completed your onboarding process. Welcome to the team!</Typography>
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical" className={styles.stepper}>
        {onboardingSteps.map((step, index) => (
          <Step key={step.label} completed={completedSteps.includes(index)}>
            <StepLabel icon={getStepIcon(index)} onClick={() => handleStepClick(index)} className={styles.stepLabel}>
              <Typography variant="h6">{step.label}</Typography>
              <Typography variant="body2" color="textSecondary">
                {step.description}
              </Typography>
            </StepLabel>
            <StepContent>
              <Box className={styles.stepContent}>{renderStepContent(index)}</Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </div>
  )
}

export default EmployeeOnboardingView
